import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { serverDetailData } from '@/mocks/serverDetail';

const specCategories = [
  { title: '核心功能与指标', items: serverDetailData.models[3].specs?.coreFeatures ?? [], icon: 'ri-cpu-line' },
  { title: '测评与认证', items: serverDetailData.models[3].specs?.certifications ?? [], icon: 'ri-shield-check-line' },
  { title: '软件运行环境', items: serverDetailData.models[3].specs?.softwareEnv ?? [], icon: 'ri-window-line' },
  { title: '输入输出接口', items: serverDetailData.models[3].specs?.ioInterface ?? [], icon: 'ri-usb-line' },
  { title: '工作环境要求', items: serverDetailData.models[3].specs?.workEnv ?? [], icon: 'ri-temp-hot-line' },
  { title: '产品外形尺寸', items: serverDetailData.models[3].specs?.dimensions ?? [], icon: 'ri-ruler-line' },
];

export default function ModelMK24Detail() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSpecTab, setActiveSpecTab] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);

  const mk24 = serverDetailData.models[3];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-8 flex items-center gap-2 text-sm text-foreground-300">
              <Link to="/" className="hover:text-primary-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to="/product-center"
                className="hover:text-primary-400 transition-colors"
              >
                产品中心
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/product-center/${slug}`}
                className="hover:text-primary-400 transition-colors"
              >
                网银U盾服务器
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-100 font-medium">{mk24.name}</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-3xl">
                <span className="text-xs font-medium text-primary-400 tracking-wide font-label">
                  {mk24.subtitle}
                </span>
                <h1 className="mt-3 text-3xl md:text-5xl font-black text-foreground-50 font-heading">
                  {mk24.name}
                </h1>
                <p className="mt-5 text-base md:text-lg text-foreground-300 leading-relaxed max-w-2xl">
                  第二代24端口桌面型网银U盾保险柜，自带密码锁机柜，内置专业版PK24服务器，支持三种锁具配置可选，7×24小时超静音稳定运行。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 产品详情主区：左大图 + 右信息 */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              {/* 左侧大图 */}
              <div className="rounded-xl overflow-hidden border border-background-200/50 bg-background-100 flex items-center justify-center">
                <img
                  src={mk24.image}
                  alt={mk24.name}
                  title={`${mk24.name} 网银U盾保险柜`}
                  className="w-full h-72 md:h-96 object-contain"
                />
              </div>

              {/* 右侧信息 */}
              <div className="flex flex-col">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  产品介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  {mk24.intro}
                </p>

                {/* 规格亮点标签 */}
                <div className="mt-5 flex flex-wrap gap-2">
                  {mk24.features.map((f) => (
                    <span
                      key={f}
                      className="px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium whitespace-nowrap"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                {/* 子型号配置 */}
                <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/50">
                  <h3 className="text-sm font-bold text-foreground-50 mb-2">可选配置</h3>
                  <div className="space-y-1.5 text-sm text-foreground-300">
                    <div className="flex items-center gap-2">
                      <i className="ri-lock-line text-primary-500" />
                      <span>CloudUSB-MK24-J：配置机械锁</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-lock-password-line text-primary-500" />
                      <span>CloudUSB-MK24-P：配置密码锁</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-fingerprint-line text-primary-500" />
                      <span>CloudUSB-MK24-PF：配置指纹密码锁</span>
                    </div>
                  </div>
                </div>

                {/* 视频播放按钮 */}
                {mk24.video && (
                  <button
                    type="button"
                    onClick={() => setVideoOpen(true)}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-300 text-sm font-medium rounded-lg border border-background-200/50 hover:border-primary-500/40 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-foreground-50">
                      <i className="ri-play-fill text-sm" />
                    </span>
                    观看产品介绍视频
                  </button>
                )}

                {/* CTA */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={`/product-center/${slug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-300 text-sm font-semibold rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line" />
                    返回产品列表
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 技术规格参数 */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                技术规格参数
              </h2>
              <p className="mt-2 text-sm text-foreground-300">
                以实际供货产品功能与指标为准，产品升级请联系销售顾问获取最新资料
              </p>
            </div>

            {/* Tab 切换 */}
            <div className="flex flex-wrap gap-2 mb-6">
              {specCategories.map((cat, idx) => (
                <button
                  key={cat.title}
                  type="button"
                  onClick={() => setActiveSpecTab(idx)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeSpecTab === idx
                      ? 'bg-primary-500 text-foreground-50'
                      : 'bg-background-50 text-foreground-300 hover:bg-background-200/60'
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>

            {/* Tab 内容 */}
            <div className="bg-background-50 rounded-xl border border-background-200/50 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                  <i className={`${specCategories[activeSpecTab].icon} text-xl`} />
                </div>
                <h3 className="text-base md:text-lg font-bold text-foreground-50 font-heading">
                  {specCategories[activeSpecTab].title}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {specCategories[activeSpecTab].items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/40"
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs" />
                    </div>
                    <span className="text-sm text-foreground-300 leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* 视频弹框 */}
      {videoOpen && mk24.video && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-foreground-950/70 backdrop-blur-sm"
          onClick={() => setVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl rounded-xl overflow-hidden bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/60 text-foreground-50 hover:bg-foreground-950/80 transition-colors cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line text-lg" />
            </button>
            <video controls autoPlay className="w-full aspect-video" poster={mk24.image}>
              <source src={mk24.video} type="video/mp4" />
              <p className="text-foreground-100 text-sm p-4">
                您的浏览器不支持视频播放。
              </p>
            </video>
          </div>
        </div>
      )}
    </div>
  );
}